import { DATE_FORMAT_SHORT, formatDate } from '@findy-code/utils/date';

import { localStorageService } from '../local-storage';
import type { LocalStorageService } from '../local-storage/local-storage.service';

// 設定キー（変える場合は要マイグレーション）
export const HAS_ANNOUNCEMENT_MODAL_OPENED_KEY =
  'has-announcement-modal-opened';
export const HAS_SHARE_ANNUAL_INCOME_V2_RESULT_KEY =
  'hasShareAnnualIncomeV2Result';
export const HAS_SHOWN_CONTRIBUTION_OF_THE_YEAR_INTRODUCTION_MODAL_KEY =
  'hasShownContributionOfTheYear2024MidIntroductionModal';
export const HAS_SHOWN_FREELANCE_MODAL_KEY = 'isShowedFreelanceModal';
export const HAS_SHOWN_INFLUENCE_SCORE_SHARE_MODAL_KEY =
  'isShowedInfluenceShareModal';
export const HAS_SHOWN_OMIKUJI_INTRODUCTION_MODAL_KEY =
  'hasShownOmikuji2025IntroductionModal';
export const IS_ENABLED_OMIKUJI_KEY = 'isEnabledOmikuji2023';
export const LAST_SHOWN_AT_KEY_PREFIX = 'lastShownAt';

// 使われなくなったキー
export const CONFIG_DEPRECATED_KEYS = [
  'hasClickedInfluenceScoreV2MeasureButton',
  'hasClickedInfluenceScoreV3MeasureButton',
  'has_clicked_phase2_measure_button',
  'isEnabledInfluenceScoreV3',
  'isShowFireworks',
  'isCompleteFireworks',
  'hasShownFireworksIntroductionModal',
  'fireworksTotalContributions',
  'hasShownInfluenceScoreV3UpdateModal',
  'isShowedSkillDeviationV2IntroductionModal',
  'isEnabledDefaultMatching',
  'isEnabledAgentSignUp',
  'isEnabledPolicy202204',
  'isEnabledTerm202204',
  'hasShownAnnualIncomeV2IntroductionModal',
  'hasShownContributionOfTheYear2021IntroductionModal',
  'hasShownOmikuji2022IntroductionModal',
  'isEnabledOmikuji2022',
  'hasShownTermsInformationModal',
  'hasShownInfluenceScoreUpdateInformation',
  'hasShownSkillDeviationV21Modal',
  'hasShownInfluenceScoreUpdateInformation',
  'hasShownContributionOfTheYear2022IntroductionModal',
  'hasShownOmikuji2023IntroductionModal',
  'hasShownOmikuji2024IntroductionModal',
  'hasEnterAnnualIncomeV2Form',
  'showJobDescriptionCount',
  'latestShowUsLpModal',
  'hasShownJobDescriptionDetails',
  'hasShownContributionOfTheYear2023IntroductionModal',
  'hasShownPfGiftCampaign',
  'isEnabledPfGiftCampaign',
] as const;

export class ConfigService {
  constructor(private readonly localStorage: LocalStorageService) {
    // 不要な値の削除
    CONFIG_DEPRECATED_KEYS.forEach((key) => this.localStorage.removeItem(key));
  }

  getHasShownContributionOfTheYearIntroductionModal() {
    const key = HAS_SHOWN_CONTRIBUTION_OF_THE_YEAR_INTRODUCTION_MODAL_KEY;

    return this.localStorage.getItem(key) === 'true';
  }

  setHasShownContributionOfTheYearIntroductionModal(value: boolean) {
    const key = HAS_SHOWN_CONTRIBUTION_OF_THE_YEAR_INTRODUCTION_MODAL_KEY;

    return this.localStorage.setItem(key, `${value}`);
  }

  getHasShownFreelanceModal() {
    const key = HAS_SHOWN_FREELANCE_MODAL_KEY;

    return this.localStorage.getItem(key) === 'true';
  }

  setHasShownFreelanceModal(value: boolean) {
    const key = HAS_SHOWN_FREELANCE_MODAL_KEY;

    return this.localStorage.setItem(key, `${value}`);
  }

  getHasShownInfluenceShareModal() {
    const key = HAS_SHOWN_INFLUENCE_SCORE_SHARE_MODAL_KEY;

    return this.localStorage.getItem(key) === 'true';
  }

  setHasShownInfluenceShareModal(value: boolean) {
    const key = HAS_SHOWN_INFLUENCE_SCORE_SHARE_MODAL_KEY;

    return this.localStorage.setItem(key, `${value}`);
  }

  getHasShownOmikujiIntroductionModal() {
    const key = HAS_SHOWN_OMIKUJI_INTRODUCTION_MODAL_KEY;

    return this.localStorage.getItem(key) === 'true';
  }

  setHasShownOmikujiIntroductionModal(value: boolean) {
    const key = HAS_SHOWN_OMIKUJI_INTRODUCTION_MODAL_KEY;

    return this.localStorage.setItem(key, `${value}`);
  }

  getHasShareAnnualIncomeV2Result() {
    const key = HAS_SHARE_ANNUAL_INCOME_V2_RESULT_KEY;

    return this.localStorage.getItem(key) === 'true';
  }

  setHasShareAnnualIncomeV2Result(value: boolean) {
    const key = HAS_SHARE_ANNUAL_INCOME_V2_RESULT_KEY;

    return this.localStorage.setItem(key, `${value}`);
  }

  getIsEnabledOmikuji() {
    const key = IS_ENABLED_OMIKUJI_KEY;

    return this.localStorage.getItem(key) === 'true';
  }

  setIsEnabledOmikuji(value: boolean) {
    const key = IS_ENABLED_OMIKUJI_KEY;

    return this.localStorage.setItem(key, `${value}`);
  }

  getHasAnnouncementModalOpened() {
    const key = HAS_ANNOUNCEMENT_MODAL_OPENED_KEY;

    return this.localStorage.getItem(key) === 'true';
  }

  setHasAnnouncementModalOpened(value: boolean) {
    const key = HAS_ANNOUNCEMENT_MODAL_OPENED_KEY;

    return this.localStorage.setItem(key, `${value}`);
  }

  getLocalMessage(matchHash: string) {
    return this.localStorage.getItem(matchHash) ?? '';
  }

  setLocalMessage(matchHash: string, message: string) {
    return this.localStorage.setItem(matchHash, message);
  }

  getLastShownAt(matchHash: string) {
    return (
      this.localStorage.getItem(`${LAST_SHOWN_AT_KEY_PREFIX}/${matchHash}`) ??
      ''
    );
  }

  setLastShownAt(matchHash: string, date: Date) {
    return this.localStorage.setItem(
      `${LAST_SHOWN_AT_KEY_PREFIX}/${matchHash}`,
      formatDate(date, DATE_FORMAT_SHORT)
    );
  }

  removeLocalMessage(matchHash: string) {
    this.localStorage.removeItem(matchHash);
  }

  removeHasAnnouncementModalOpened() {
    const key = HAS_ANNOUNCEMENT_MODAL_OPENED_KEY;

    this.localStorage.removeItem(key);
  }
}

export const configService = new ConfigService(localStorageService);
